class ProductStickyBarModule {
  constructor(controller) {
    this.controller = controller;
    this.section = controller.section;
    this.bar = controller.query('[data-sticky-bar]');
    this.form = controller.query('[data-product-form]');
    this.variantSelect = controller.query('[data-variant-select]');
    this.primarySubmitButton = controller.query('[data-submit-button]');
    this.quantityInput = controller.query('[data-quantity-input]');
    this.name = this.bar?.querySelector('[data-sticky-bar-name]');
    this.priceValue = this.bar?.querySelector('[data-sticky-bar-price-value]');
    this.image = this.bar?.querySelector('[data-sticky-bar-image]');
    this.button = this.bar?.querySelector('[data-sticky-bar-button]');
    this.productTitle = this.bar?.dataset.productTitle || '';
    this.buttonText = this.bar?.dataset.buttonText || 'Order Now ->';
    this.soldOutText = this.bar?.dataset.soldOutText || 'Sold Out';
    this.directCheckout = this.bar?.dataset.directCheckout === 'true';
    this.primaryScrolledPast = false;
    this.currentAvailability = !this.button?.disabled;
    this.visibilityFrame = null;

    if (!this.bar || !this.button) return;

    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleScrollFallback = this.handleScrollFallback.bind(this);
    this.handleStickyButtonClick = this.handleStickyButtonClick.bind(this);
    this.scheduleVisibilityCheck = this.scheduleVisibilityCheck.bind(this);

    this.bindEvents();
    this.syncInitialState();
  }

  bindEvents() {
    this.section.addEventListener('variant:change', this.handleVariantChange);
    this.button.addEventListener('click', this.handleStickyButtonClick);

    this.observePrimaryButton();
    this.observePrimarySubmitState();
  }

  syncInitialState() {
    const initialVariant = this.variantSelect?.selectedOptions[0];
    if (initialVariant) {
      this.updateFromVariantOption(initialVariant);
    }

    this.syncButtonState();
    this.updateVisibility();
  }

  handleVariantChange(event) {
    this.updateFromVariantOption(event.detail?.variantOption);
  }

  async handleStickyButtonClick() {
    if (this.button.disabled) return;

    if (this.directCheckout) {
      await this.handleDirectCheckout();
      return;
    }

    if (this.form?.requestSubmit && this.primarySubmitButton) {
      this.form.requestSubmit(this.primarySubmitButton);
      return;
    }

    if (this.primarySubmitButton) {
      this.primarySubmitButton.click();
      return;
    }

    await this.handleFallbackAddToCart();
  }

  buildProductName(variantOption) {
    const variantTitle = (variantOption?.dataset.variantTitle || variantOption?.textContent || '').trim();

    if (!variantTitle || variantTitle.toLowerCase() === 'default title') {
      return this.productTitle;
    }

    return this.productTitle ? `${this.productTitle} - ${variantTitle}` : variantTitle;
  }

  updateFromVariantOption(variantOption) {
    if (!variantOption) return;

    if (this.name) {
      this.name.textContent = this.buildProductName(variantOption);
    }

    if (this.priceValue) {
      const fallbackPrice = Number.parseInt(variantOption.dataset.variantPrice, 10) || 0;
      this.priceValue.textContent =
        variantOption.dataset.variantPriceFormatted || Money.format(fallbackPrice);
    }

    if (this.image) {
      const variantImage = variantOption.dataset.variantImage || '';
      const variantImageAlt = variantOption.dataset.variantImageAlt || this.productTitle;

      if (variantImage) {
        this.image.src = variantImage;
        this.image.alt = variantImageAlt;
        this.image.hidden = false;
      } else {
        this.image.hidden = true;
      }
    }

    const available = variantOption.dataset.variantAvailable === 'true';
    this.currentAvailability = available;
    this.button.textContent = available ? this.buttonText : this.soldOutText;
    this.bar.classList.toggle('product-sticky-bar--sold-out', !available);

    this.syncButtonState();
  }

  observePrimaryButton() {
    this.visibilityTarget = this.primarySubmitButton;
    if (!this.visibilityTarget) {
      return;
    }

    window.addEventListener('scroll', this.scheduleVisibilityCheck, { passive: true });
    window.addEventListener('resize', this.scheduleVisibilityCheck);

    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver(() => {
        this.scheduleVisibilityCheck();
      },
      {
        threshold: 0.2
      }
      );

      this.visibilityObserver.observe(this.visibilityTarget);
    }

    this.handleScrollFallback();
  }

  scheduleVisibilityCheck() {
    if (this.visibilityFrame !== null) return;

    this.visibilityFrame = window.requestAnimationFrame(() => {
      this.visibilityFrame = null;
      this.handleScrollFallback();
    });
  }

  handleScrollFallback() {
    if (!this.visibilityTarget) return;

    const rect = this.visibilityTarget.getBoundingClientRect();
    this.primaryScrolledPast = rect.bottom <= 0;
    this.updateVisibility();
  }

  observePrimarySubmitState() {
    if (!this.primarySubmitButton || !('MutationObserver' in window)) {
      return;
    }

    this.primarySubmitObserver = new MutationObserver(() => {
      this.syncButtonState();
    });

    this.primarySubmitObserver.observe(this.primarySubmitButton, {
      attributes: true,
      attributeFilter: ['class', 'disabled']
    });
  }

  syncButtonState() {
    const selectedVariant = this.variantSelect?.selectedOptions[0];
    const variantAvailable = selectedVariant
      ? selectedVariant.dataset.variantAvailable === 'true'
      : this.currentAvailability;
    const primaryDisabled = this.primarySubmitButton?.disabled || false;
    const isLoading = this.primarySubmitButton?.classList.contains('loading') || false;

    this.button.disabled = !variantAvailable || primaryDisabled;
    this.button.classList.toggle('loading', isLoading);
  }

  getQuantity() {
    const value = Number.parseInt(this.quantityInput?.value, 10);
    if (!this.quantityInput) return 1;

    const min = Number.parseInt(this.quantityInput.min, 10) || 1;
    const max = Number.parseInt(this.quantityInput.max, 10) || 99;
    const safeValue = Number.isNaN(value) ? min : Math.min(Math.max(value, min), max);
    this.quantityInput.value = `${safeValue}`;
    return safeValue;
  }

  getSelectedVariantOption() {
    return this.variantSelect?.selectedOptions?.[0] || null;
  }

  buildFormData() {
    const formData = this.form ? new FormData(this.form) : new FormData();
    const variantId = this.variantSelect?.value || this.getSelectedVariantOption()?.value || '';

    if (variantId) {
      formData.set('id', variantId);
    }

    formData.set('quantity', `${this.getQuantity()}`);
    return formData;
  }

  setRequestState(isLoading) {
    if (this.primarySubmitButton) {
      this.primarySubmitButton.classList.toggle('loading', isLoading);

      if (isLoading) {
        this.primarySubmitButton.setAttribute('disabled', 'disabled');
      } else if (this.currentAvailability) {
        this.primarySubmitButton.removeAttribute('disabled');
      }
    }

    this.button.classList.toggle('loading', isLoading);
    this.syncButtonState();
  }

  async handleDirectCheckout() {
    this.setRequestState(true);

    try {
      const formData = this.buildFormData();
      const variantData = this.getSelectedVariantOption();

      if (window.CartApi?.addToCart) {
        await CartApi.addToCart(formData, { variantData });
      } else {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || 'Could not add to cart');
        }
      }

      const rootPath = window.Shopify?.routes?.root || '/';
      window.location.assign(new URL('checkout', `${window.location.origin}${rootPath}`).toString());
    } catch (error) {
      if (window.CartApi?.showToast) {
        CartApi.showToast(error.message, true);
      } else {
        console.error(error);
      }

      this.setRequestState(false);
    }
  }

  async handleFallbackAddToCart() {
    this.setRequestState(true);

    try {
      const formData = this.buildFormData();
      const variantData = this.getSelectedVariantOption();
      let cartResponse = null;

      if (window.CartApi?.addToCart) {
        cartResponse = await CartApi.addToCart(formData, { variantData });
        await CartApi.refreshCart?.(cartResponse);
      } else {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || 'Could not add to cart');
        }
      }

      document.dispatchEvent(new CustomEvent('cart:added', { detail: cartResponse }));
      if (!window.SimpleHeaderCart?.open) {
        (
          document.getElementById('simple-header-cart-trigger') ||
          document.getElementById('custom-header-cart-trigger')
        )?.click();
      }
    } catch (error) {
      if (window.CartApi?.showToast) {
        CartApi.showToast(error.message, true);
      } else {
        console.error(error);
      }
    } finally {
      this.setRequestState(false);
    }
  }

  updateVisibility() {
    const shouldShow = this.primaryScrolledPast;
    this.bar.classList.toggle('visible', shouldShow);
  }
}

window.HerbiProductSection.registerModule('sticky-bar', (controller) => {
  return new ProductStickyBarModule(controller);
});
